import { createRef } from 'react'
import type { DropzoneRef } from 'react-dropzone'
import Dropzone from 'react-dropzone'

import { observer } from 'mobx-react'

import AvatarUploader from 'modules/core/components/AvatarUploader'
import Lightbox from 'modules/core/components/LightBox'
import Popover from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'

import type UserForm from 'modules/employees/lib/forms/Personal/User'

interface Props {
  userForm: UserForm
  fullName: string
  avatar: string
  avatarFull: string
  readonly?: boolean
  onDeleteAvatar: () => void
}

const AvatarForm = ({
  userForm,
  fullName,
  avatar,
  avatarFull,
  onDeleteAvatar,
  readonly,
}: Props) => {
  const dropzoneRef = createRef<DropzoneRef>()
  const lightBoxTriggerRef = createRef<HTMLDivElement>()

  const onDrop = (files: File[]) => {
    userForm.get('avatar').set(files[0])
  }

  const openDropzone = () => {
    dropzoneRef.current?.open()
  }

  const showOriginalAvatar = () => {
    lightBoxTriggerRef?.current?.click()
  }

  const hideTooltips = !userForm.get('avatar').value

  if (readonly) {
    if (avatarFull) {
      return (
        <Lightbox url={avatarFull}>
          <UserAvatar size="s120" user={{ avatar, fullName }} />
        </Lightbox>
      )
    }

    return <UserAvatar size="s120" user={{ avatar, fullName }} />
  }

  if (hideTooltips) {
    return <AvatarUploader size="s120" avatarField={userForm.get('avatar')} />
  }

  return (
    <>
      <Lightbox url={avatarFull}>
        <div ref={lightBoxTriggerRef}></div>
      </Lightbox>
      <Popover type="list">
        <Popover.Trigger>
          <AvatarUploader size="s120" avatarField={userForm.get('avatar')} readonly />
        </Popover.Trigger>
        <Popover.Content side="top" align="end">
          <Popover.TouchableOption
            title={i18n.t('employee_form.avatar.view_original')}
            onClick={showOriginalAvatar}
            disabled={!avatarFull.trim()}
          />
          <Popover.TouchableOption
            title={i18n.t('employee_form.avatar.new')}
            onClick={openDropzone}
          />
          <Popover.TouchableOption
            title={i18n.t('employee_form.avatar.delete')}
            onClick={onDeleteAvatar}
            danger
          />
        </Popover.Content>
      </Popover>
      <Dropzone onDrop={onDrop} multiple={false} ref={dropzoneRef}>
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()} style={{ visibility: 'hidden' }}>
            <input {...getInputProps()} />
          </div>
        )}
      </Dropzone>
    </>
  )
}

export default observer(AvatarForm)
